import React, { useState, useEffect } from 'react';
import { getUserPointTransactions } from '../apiClient';
import { useJWTAuth } from '../JWTAuthContext';

interface PointTransaction {
  id: number;
  transaction_type: string;
  points_amount: number;
  quiz_id?: number;
  quiz_score_percentage?: number;
  quiz_total_questions?: number;
  quiz_skipped_questions?: number;
  quiz_level?: string;
  earning_rule_applied?: string;
  bonus_type?: string;
  ai_tutor_minutes?: number;
  week_start_date: string;
  total_points_after: number;
  created_at: string;
}

interface TransactionHistoryData {
  transactions: PointTransaction[];
  pagination: {
    limit: number;
    offset: number;
    total_returned: number;
    has_more: boolean;
  };
}

const PointTransactionHistory: React.FC = () => {
  const { user } = useJWTAuth();
  const [transactionData, setTransactionData] = useState<TransactionHistoryData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<string>('');
  const [currentOffset, setCurrentOffset] = useState(0);

  const fetchTransactions = async (offset = 0, transactionType = '') => {
    if (user && user.id) {
      setIsLoading(true);
      setError(null);
      
      try {
        const options = {
          limit: 5,
          offset: offset,
          transactionType: transactionType || null
        };
        
        const response = await getUserPointTransactions(user.id, options);
        if (response && response.status === 'success' && response.result) {
          if (offset === 0) {
            setTransactionData(response.result);
          } else {
            // Append to existing data for pagination
            setTransactionData(prev => ({
              ...response.result,
              transactions: [...(prev?.transactions || []), ...response.result.transactions]
            }));
          }
        } else {
          setError('Failed to load transaction history');
        }
      } catch (error) {
        console.error('Failed to fetch transactions:', error);
        setError('Error loading transaction data');
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    setCurrentOffset(0);
    fetchTransactions(0, filterType);
  }, [user, filterType]);

  const loadMore = () => {
    if (transactionData && transactionData.pagination.has_more) {
      const newOffset = currentOffset + transactionData.pagination.limit;
      setCurrentOffset(newOffset);
      fetchTransactions(newOffset, filterType);
    }
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'quiz_completion':
        return 'fas fa-graduation-cap text-success';
      case 'ai_tutor':
        return 'fas fa-robot text-info';
      case 'admin_adjustment':
        return 'fas fa-user-shield text-warning';
      default:
        return 'fas fa-coins text-primary';
    }
  };

  const getTransactionDescription = (transaction: PointTransaction) => {
    switch (transaction.transaction_type) {
      case 'quiz_completion':
        let description = `Quiz completed`;
        if (transaction.quiz_score_percentage) {
          description += ` (${transaction.quiz_score_percentage.toFixed(1)}%)`;
        }
        if (transaction.quiz_level) {
          description += ` - ${transaction.quiz_level.charAt(0).toUpperCase() + transaction.quiz_level.slice(1)} level`;
        }
        if (transaction.bonus_type) {
          description += ` + ${transaction.bonus_type.replace('_', ' ')} bonus`;
        }
        return description;
      
      case 'ai_tutor':
        return `AI Tutor session${transaction.ai_tutor_minutes ? ` (${transaction.ai_tutor_minutes} minutes)` : ''}`;
      
      case 'admin_adjustment':
        return 'Admin adjustment';
      
      default:
        return transaction.transaction_type.replace('_', ' ');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (isLoading && !transactionData) {
    return (
      <div className="card">
        <div className="card-body d-flex align-items-center justify-content-center" style={{ minHeight: '200px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="card-body">
          <div className="alert alert-warning" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <div className="row align-items-center">
          <div className="col">
            <h5 className="card-title mb-0">
              <i className="fas fa-history me-2"></i>
              Point Transaction History
            </h5>
          </div>
          <div className="col-auto">
            <select 
              className="form-select form-select-sm"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="">All Transactions</option>
              <option value="quiz_completion">Quiz Completions</option>
              <option value="ai_tutor">AI Tutor Sessions</option>
              <option value="admin_adjustment">Admin Adjustments</option>
            </select>
          </div>
        </div>
      </div>

      <div className="card-body p-0">
        {transactionData && transactionData.transactions.length > 0 ? (
          <>
            <div className="list-group list-group-flush">
              {transactionData.transactions.map((transaction) => (
                <div key={transaction.id} className="list-group-item">
                  <div className="row align-items-center">
                    <div className="col-auto">
                      <div className="avatar avatar-sm rounded-circle">
                        <i className={getTransactionIcon(transaction.transaction_type)}></i>
                      </div>
                    </div>
                    
                    <div className="col">
                      <div className="fw-bold">
                        {getTransactionDescription(transaction)}
                      </div>
                      <small className="text-muted">
                        {formatDate(transaction.created_at)}
                        {transaction.earning_rule_applied && (
                          <span className="ms-2 badge bg-light text-dark">
                            {transaction.earning_rule_applied.replace('_', ' ')}
                          </span>
                        )}
                      </small>
                    </div>
                    
                    <div className="col-auto text-end">
                      <div className={`fw-bold ${transaction.points_amount >= 0 ? 'text-success' : 'text-danger'}`}>
                        {transaction.points_amount >= 0 ? '+' : ''}{transaction.points_amount}
                      </div>
                      <small className="text-muted">
                        Total: {transaction.total_points_after.toLocaleString()}
                      </small>
                    </div>
                  </div>
                  
                  {/* Additional Details for Quiz Transactions */}
                  {transaction.transaction_type === 'quiz_completion' && (
                    <div className="row mt-2">
                      <div className="col-12">
                        <div className="row text-center">
                          {transaction.quiz_total_questions && (
                            <div className="col-3">
                              <small className="text-muted">Questions</small>
                              <div className="fw-bold">{transaction.quiz_total_questions}</div>
                            </div>
                          )}
                          {transaction.quiz_skipped_questions !== undefined && (
                            <div className="col-3">
                              <small className="text-muted">Skipped</small>
                              <div className="fw-bold">{transaction.quiz_skipped_questions}</div>
                            </div>
                          )}
                          {transaction.quiz_score_percentage && (
                            <div className="col-3">
                              <small className="text-muted">Score</small>
                              <div className="fw-bold">{transaction.quiz_score_percentage.toFixed(1)}%</div>
                            </div>
                          )}
                          {transaction.quiz_level && (
                            <div className="col-3">
                              <small className="text-muted">Level</small>
                              <div className="fw-bold text-capitalize">{transaction.quiz_level}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Load More Button */}
            {transactionData.pagination.has_more && (
              <div className="card-footer text-center">
                <button 
                  className="btn btn-outline-primary"
                  onClick={loadMore}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Loading...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-chevron-down me-2"></i>
                      Load More
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-5">
            <i className="fas fa-history text-muted mb-3" style={{ fontSize: '3rem' }}></i>
            <h5 className="text-muted">No Transactions Found</h5>
            <p className="text-muted">
              {filterType ? 
                `No ${filterType.replace('_', ' ')} transactions available.` : 
                'Complete quizzes to start earning points and see your transaction history here.'
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PointTransactionHistory;